if (!customElements.get('search-filter')) {
  class SearchFilter extends HTMLElement {
    connectedCallback() {
      this.init();
    }

    /*
     * ============================================================
     * SEARCH INPUTS
     * ============================================================
     */
    bindSearchInputs() {
      const inputs = this.querySelectorAll('.filter-search-input');

      inputs.forEach((input) => {
        /*
         * Prevent binding the same input more than once.
         */
        if (input.dataset.searchBound === 'true') {
          return;
        }

        input.dataset.searchBound = 'true';

        const details = input.closest('details');

        if (!details) {
          return;
        }

        const dropdown = details.querySelector('.filter-select');

        if (!dropdown) {
          return;
        }

        /*
         * Clicking inside the text input should not cause
         * unwanted <details> toggling.
         */
        input.addEventListener('click', (event) => {
          event.stopPropagation();
        });

        /*
         * Search/filter options as the customer types.
         */
        input.addEventListener('input', () => {
          const searchValue = input.value
            .trim()
            .toLowerCase();

          const items = details.querySelectorAll(
            '.filter-select__item:not(.holder)'
          );

          /*
           * If empty, show every option again.
           */
          if (searchValue === '') {
            items.forEach((item) => {
              item.style.display = '';
            });

            dropdown.classList.remove('has-results');
            details.setAttribute('open', '');

            return;
          }

          let hasVisibleItems = false;

          items.forEach((item) => {
            const itemText = item.textContent
              .trim()
              .toLowerCase();

            const matches =
              itemText.startsWith(searchValue);

            item.style.display = matches ? '' : 'none';

            if (matches) {
              hasVisibleItems = true;
            }
          });

          if (hasVisibleItems) {
            details.setAttribute('open', '');
            dropdown.classList.add('has-results');
          } else {
            dropdown.classList.remove('has-results');
          }
        });
      });
    }

    /*
     * ============================================================
     * INIT
     * ============================================================
     */
    async init() {
      this.elements = {
        filterDetails:
          this.querySelectorAll('details'),

        loadingSpinner:
          this.querySelector('.loading__spinner'),
      };

      /*
       * Load the real Shopify filter values first.
       */
      await this.initialFilterRendering();

      this.elements.filterItems =
        this.querySelectorAll(
          '.filter-select__item:not(.holder)'
        );

      this.elements.filterSubmitButton =
        this.querySelector('.filter-value');

      this.elements.conditionTabs =
        this.querySelectorAll(
          '[data-condition-filter]'
        );

      this.activeConditionFilter = '';

      /*
       * Condition tabs, if the theme has them.
       */
      this.elements.conditionTabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          this.elements.conditionTabs.forEach(
            (item) => {
              item.classList.remove('active');
            }
          );

          tab.classList.add('active');

          this.activeConditionFilter =
            tab.dataset.conditionFilter || '';

          this.updateFilters();
        });
      });

      if (!this.elements.filterSubmitButton) {
        return;
      }

      /*
       * Save the clean collection URL.
       */
      if (
        !this.elements.filterSubmitButton.hasAttribute(
          'data-previous-href'
        )
      ) {
        this.elements.filterSubmitButton.setAttribute(
          'data-previous-href',
          this.elements.filterSubmitButton.getAttribute(
            'href'
          ) || ''
        );
      }

      this.previousHref =
        this.elements.filterSubmitButton.getAttribute(
          'data-previous-href'
        ) || '';

      /*
       * Details/dropdown behavior.
       */
      this.elements.filterDetails.forEach((item) => {
        item.addEventListener(
          'click',
          this.closeDetails.bind(this, item)
        );

        const summary =
          item.querySelector('summary');

        if (summary) {
          summary.setAttribute(
            'tabindex',
            '0'
          );

          summary.addEventListener(
            'keydown',
            this.handleKeyDown.bind(
              this,
              item
            )
          );

          item.addEventListener(
            'focusout',
            this.handleFocusOut.bind(
              this,
              item
            )
          );
        }
      });

      document.addEventListener(
        'click',
        this.closeAllDetails.bind(this)
      );

      this.bindFilterItems();
      this.bindSearchInputs();
    }

    /*
     * ============================================================
     * FILTER ITEM EVENTS
     * ============================================================
     */
    bindFilterItems() {
      this.elements.filterItems =
        this.querySelectorAll(
          '.filter-select__item:not(.holder)'
        );

      this.elements.filterItems.forEach(
        (item) => {
          /*
           * Prevent duplicate listeners.
           */
          if (item.dataset.filterBound === 'true') {
            return;
          }

          item.dataset.filterBound = 'true';

          item.addEventListener(
            'click',
            (event) => {
              event.preventDefault();
              event.stopPropagation();

              this.onClick(item);
            }
          );

          item.setAttribute(
            'tabindex',
            item.classList.contains('disabled')
              ? '-1'
              : '0'
          );

          item.addEventListener(
            'keydown',
            this.handleItemKeyDown.bind(
              this,
              item
            )
          );
        }
      );
    }

    /*
     * ============================================================
     * KEYBOARD
     * ============================================================
     */
    handleKeyDown(details, event) {
      /*
       * Don't let keyboard handling on summary interfere
       * with typing in the search field.
       */
      if (
        event.target.matches(
          '.filter-search-input'
        )
      ) {
        return;
      }

      if (
        event.key === 'Enter' ||
        event.key === ' '
      ) {
        event.preventDefault();

        details.open = !details.open;
      }
    }

    handleItemKeyDown(item, event) {
      if (
        event.key === 'Enter' ||
        event.key === ' '
      ) {
        event.preventDefault();

        this.onClick(item);
      }
    }

    /*
     * ============================================================
     * DETAILS OPEN/CLOSE
     * ============================================================
     */
    closeDetails(clickedItem) {
      this.elements.filterDetails.forEach(
        (item) => {
          if (item !== clickedItem) {
            item.removeAttribute('open');
          }
        }
      );
    }

    closeAllDetails(event) {
      const clickedInside =
        Array.from(
          this.elements.filterDetails
        ).some((item) =>
          item.contains(event.target)
        );

      if (!clickedInside) {
        this.elements.filterDetails.forEach(
          (item) => {
            item.removeAttribute('open');
          }
        );
      }
    }

    handleFocusOut(details) {
      setTimeout(() => {
        if (
          !details.contains(
            document.activeElement
          )
        ) {
          details.removeAttribute('open');
        }
      }, 0);
    }

    /*
     * ============================================================
     * OPTION CLICK
     * ============================================================
     */
    onClick(clickedItem) {
      /*
       * Do nothing when option is unavailable.
       */
      if (
        clickedItem.classList.contains(
          'disabled'
        ) ||
        clickedItem.getAttribute(
          'aria-disabled'
        ) === 'true'
      ) {
        return;
      }

      const parentDetails =
        clickedItem.closest('details');

      if (!parentDetails) {
        return;
      }

      const summaryElement =
        parentDetails.querySelector(
          'summary span'
        );

      if (summaryElement) {
        const input =
          summaryElement.querySelector(
            '.filter-search-input'
          );

        if (input) {
          input.value =
            clickedItem.textContent.trim();
        } else {
          summaryElement.textContent =
            clickedItem.textContent.trim();
        }
      }

      /*
       * Set only one active value per dropdown.
       */
      const otherItems =
        parentDetails.querySelectorAll(
          '.filter-select__item:not(.holder)'
        );

      otherItems.forEach((item) => {
        if (item === clickedItem) {
          item.classList.add('active');

          item.setAttribute(
            'aria-selected',
            'true'
          );
        } else {
          item.classList.remove('active');

          item.removeAttribute(
            'aria-selected'
          );
        }
      });

      /*
       * Show all options again after selection
       * so a previous text search doesn't remain.
       */
      otherItems.forEach((item) => {
        item.style.display = '';
      });

      parentDetails.removeAttribute('open');

      /*
       * Recalculate combinations:
       * Brand -> valid Car Types
       * Car Type -> valid Brands
       */
      this.updateFilters();
    }

    /*
     * ============================================================
     * BUILD FILTER URL
     * ============================================================
     */
    getActiveFilterData() {
      const activeItems =
        this.querySelectorAll(
          '.filter-select__item.active:not(.holder)'
        );

      const dropdownFilters =
        Array.from(activeItems)
          .map((item) =>
            item.getAttribute('data-filter')
          )
          .filter(Boolean);

      const allFilters = [];

      if (this.activeConditionFilter) {
        allFilters.push(
          this.activeConditionFilter
        );
      }

      allFilters.push(...dropdownFilters);

      return allFilters;
    }

    buildFilterUrl() {
      const allFilters =
        this.getActiveFilterData();

      const dataFilterValues =
        allFilters.join('&');

      const currentValue =
        this.previousHref || '';

      return dataFilterValues
        ? `${currentValue}?${dataFilterValues}`
        : currentValue;
    }

    /*
     * ============================================================
     * FIND FACET BY LABEL
     * ============================================================
     */
    findMatchingFacet(parsedHTML, filterLabel) {
      if (!filterLabel) {
        return null;
      }

      const normalizedTarget =
        filterLabel
          .trim()
          .toLowerCase();

      /*
       * Desktop facets.
       */
      const desktopSummaries =
        parsedHTML.querySelectorAll(
          '.facets__summary'
        );

      for (
        const summary of desktopSummaries
      ) {
        const text =
          summary.textContent
            .trim()
            .toLowerCase();

        if (
          text === normalizedTarget ||
          text.includes(normalizedTarget)
        ) {
          return (
            summary.parentElement ||
            summary.closest('details')
          );
        }
      }

      /*
       * Mobile facets.
       */
      const mobileSummaries =
        parsedHTML.querySelectorAll(
          '.mobile-facets__summary'
        );

      for (
        const summary of mobileSummaries
      ) {
        const text =
          summary.textContent
            .trim()
            .toLowerCase();

        if (
          text === normalizedTarget ||
          text.includes(normalizedTarget)
        ) {
          return (
            summary.parentElement ||
            summary.closest('details')
          );
        }
      }

      return null;
    }

    /*
     * ============================================================
     * READ SHOPIFY FACET VALUE AVAILABILITY
     * ============================================================
     */
    getShopifyValueAvailability(
      shopifyValue
    ) {
      /*
       * IMPORTANT:
       *
       * Default is TRUE.
       *
       * We only mark an option unavailable when Shopify
       * explicitly tells us:
       *
       * - count is 0
       * - input is disabled
       * - aria-disabled=true
       * - disabled class is present
       *
       * This prevents valid Audi/SUV etc. values from
       * incorrectly becoming faded.
       */
      let available = true;

      const container =
        shopifyValue.closest(
          [
            'li',
            'label',
            '.facet-checkbox',
            '.facets__item',
            '.mobile-facets__item',
            '.mobile-facets__label',
          ].join(',')
        ) || shopifyValue;

      /*
       * Find input.
       */
      let input = null;

      if (
        shopifyValue.matches('input')
      ) {
        input = shopifyValue;
      } else {
        input =
          shopifyValue.querySelector(
            'input'
          ) ||
          container.querySelector(
            'input'
          );
      }

      /*
       * 1. Explicit count data attribute.
       */
      const possibleCounts = [
        shopifyValue.dataset
          .filterValueCount,

        container.dataset
          .filterValueCount,

        shopifyValue.dataset.count,

        container.dataset.count,
      ];

      for (
        const rawCount of possibleCounts
      ) {
        if (
          rawCount === undefined ||
          rawCount === null ||
          rawCount === ''
        ) {
          continue;
        }

        const count =
          parseInt(rawCount, 10);

        if (!Number.isNaN(count)) {
return count >= 1;        }
      }

      /*
       * 2. Search count elements commonly used
       * by Shopify themes.
       */
      const countElement =
        container.querySelector(
          [
            '.facets__selected',
            '.facet-checkbox__text-label',
            '.facet-checkbox__text',
            '.mobile-facets__label',
            '[data-count]',
            '.count',
          ].join(',')
        );

      const text =
        (
          countElement
            ? countElement.textContent
            : container.textContent
        ) || '';

      /*
       * Match:
       *
       * SUV (10)
       * SUV (0)
       */
      const parenthesisMatch =
        text.match(/\((\d+)\)/);

      if (parenthesisMatch) {
        const count =
          parseInt(
            parenthesisMatch[1],
            10
          );

        if (!Number.isNaN(count)) {
          return count >= 1;
        }
      }

      /*
       * Some Shopify themes expose a count as
       * a standalone number using data-count.
       */
      const explicitCountElement =
        container.querySelector(
          '[data-count]'
        );

      if (explicitCountElement) {
        const countValue =
          explicitCountElement.dataset
            .count;

        if (
          countValue !== undefined
        ) {
          const count =
            parseInt(
              countValue,
              10
            );

          if (!Number.isNaN(count)) {
return count >= 1;          }
        }
      }

      /*
       * 3. Input disabled state.
       */
      if (input) {
        if (
          input.disabled ||
          input.hasAttribute(
            'disabled'
          )
        ) {
          return false;
        }

        /*
         * An enabled input means Shopify considers
         * the value selectable.
         */
        return true;
      }

      /*
       * 4. aria-disabled.
       */
      if (
        shopifyValue.getAttribute(
          'aria-disabled'
        ) === 'true' ||
        container.getAttribute(
          'aria-disabled'
        ) === 'true'
      ) {
        return false;
      }

      /*
       * 5. Disabled classes.
       */
      if (
        shopifyValue.classList.contains(
          'disabled'
        ) ||
        container.classList.contains(
          'disabled'
        )
      ) {
        return false;
      }

      /*
       * Shopify did not explicitly say unavailable.
       */
      return available;
    }

    /*
     * ============================================================
     * UPDATE AVAILABILITY AFTER SELECTING BRAND / CAR TYPE
     * ============================================================
     */
    async updateFilters() {
      if (
        !this.elements
          .filterSubmitButton
      ) {
        return;
      }

      const url =
        this.buildFilterUrl();

      /*
       * Update Search button immediately.
       */
      this.elements.filterSubmitButton.setAttribute(
        'href',
        url
      );

      this.elements.filterSubmitButton.removeAttribute(
        'aria-disabled'
      );

      try {
        const response =
          await fetch(url);

        if (!response.ok) {
          throw new Error(
            `Filter request failed: ${response.status}`
          );
        }

        const responseText =
          await response.text();

        const parsedHTML =
          new DOMParser().parseFromString(
            responseText,
            'text/html'
          );

        /*
         * Process Brand and Car Type independently.
         */
        this.elements.filterDetails.forEach(
          (detail) => {
            const filterLabel =
              detail.dataset.filterLabel;

            if (!filterLabel) {
              return;
            }

            const matchingFacet =
              this.findMatchingFacet(
                parsedHTML,
                filterLabel
              );

            /*
             * If Shopify does not return this facet,
             * do NOT disable everything.
             */
            if (!matchingFacet) {
              return;
            }

            const shopifyValues =
              matchingFacet.querySelectorAll(
                '[data-filter-value-name]'
              );

            /*
             * Again: if Shopify's HTML does not contain
             * these values, don't disable all custom items.
             */
            if (!shopifyValues.length) {
              return;
            }

            const customItems =
              detail.querySelectorAll(
                '.filter-select__item:not(.holder)'
              );

            customItems.forEach(
              (customItem) => {
                const customName =
                  customItem.textContent
                    .trim()
                    .toLowerCase();

                let found = false;

                /*
                 * Default available.
                 */
                let available = true;

                for (
                  const shopifyValue
                  of shopifyValues
                ) {
                  const valueName =
                    (
                      shopifyValue.dataset
                        .filterValueName ||
                      ''
                    )
                      .trim()
                      .toLowerCase();

                  if (
                    valueName !==
                    customName
                  ) {
                    continue;
                  }

                  found = true;

                  available =
                    this.getShopifyValueAvailability(
                      shopifyValue
                    );

                  break;
                }

                /*
                 * Reset previous state first.
                 */
                customItem.classList.remove(
                  'disabled'
                );

                customItem.removeAttribute(
                  'disabled'
                );

                customItem.removeAttribute(
                  'aria-disabled'
                );

                customItem.setAttribute(
                  'tabindex',
                  '0'
                );

                /*
                 * Currently selected value should remain
                 * visible/selectable in the UI.
                 */
                if (
                  customItem.classList.contains(
                    'active'
                  )
                ) {
                  return;
                }

                /*
                 * CRITICAL FIX:
                 *
                 * Only disable if Shopify actually found
                 * this exact value AND explicitly reported
                 * that it isn't available.
                 *
                 * Do NOT disable simply because our script
                 * failed to understand Shopify's markup.
                 */
                if (
                  found &&
                  available === false
                ) {
                  customItem.classList.add(
                    'disabled'
                  );

                  customItem.setAttribute(
                    'aria-disabled',
                    'true'
                  );

                  customItem.setAttribute(
                    'tabindex',
                    '-1'
                  );
                }
              }
            );
          }
        );
      } catch (error) {
        /*
         * IMPORTANT:
         *
         * Don't disable everything when the fetch fails.
         */
        console.error(
          'Vehicle filter error:',
          error
        );
      }
    }

    /*
     * ============================================================
     * INITIAL FILTER LOAD
     * ============================================================
     */
    async initialFilterRendering() {
      if (
        this.elements.loadingSpinner
      ) {
        this.elements.loadingSpinner.classList.remove(
          'hidden'
        );
      }

      this.setAttribute(
        'data-loading',
        'true'
      );

      try {
        await this.loadFilters(
          this.dataset.filterUrl
        );
      } catch (error) {
        console.error(
          'Initial vehicle filter error:',
          error
        );
      }

      this.removeAttribute(
        'data-loading'
      );

      if (
        this.elements.loadingSpinner
      ) {
        this.elements.loadingSpinner.classList.add(
          'hidden'
        );
      }
    }

    /*
     * ============================================================
     * LOAD BRAND / CAR TYPE VALUES FROM SHOPIFY
     * ============================================================
     */
    async loadFilters(url) {
      if (
        !url ||
        !this.elements.filterDetails
      ) {
        return;
      }

      const res =
        await fetch(url);

      if (!res.ok) {
        throw new Error(
          `Initial filter request failed: ${res.status}`
        );
      }

      const html =
        await res.text();

      const parsedHTML =
        new DOMParser().parseFromString(
          html,
          'text/html'
        );

      const filtersForm =
        parsedHTML.querySelector(
          'facet-filters-form'
        );

      if (!filtersForm) {
        return;
      }

      let summarySelector =
        '.facets__summary-label';

      let filters =
        filtersForm.querySelectorAll(
          '.facets__summary'
        );

      /*
       * Mobile fallback.
       */
      if (filters.length === 0) {
        filters =
          filtersForm.querySelectorAll(
            '.mobile-facets__summary'
          );

        summarySelector =
          'div > span';
      }

      if (filters.length === 0) {
        return;
      }

      this.elements.filterDetails.forEach(
        (detail) => {
          const summary =
            detail.querySelector(
              'summary > span'
            );

          const filterSelect =
            detail.querySelector(
              '.filter-select'
            );

          if (
            !summary ||
            !filterSelect
          ) {
            return;
          }

          const summaryText =
            detail.dataset.filterLabel ||
            summary.innerText.trim();

          filters.forEach((filter) => {
            const filterLabel =
              filter.querySelector(
                summarySelector
              );

            if (!filterLabel) {
              return;
            }

            const filterLabelText =
              filterLabel.innerText
                .trim()
                .toLowerCase();

            const targetText =
              summaryText
                .trim()
                .toLowerCase();

            const isEqual =
              filterLabelText ===
                targetText ||
              filterLabelText.includes(
                targetText
              );

            if (!isEqual) {
              return;
            }

            const facetContainer =
              filter.parentElement ||
              filter.closest('details');

            if (!facetContainer) {
              return;
            }

            const filterValues =
              facetContainer.querySelectorAll(
                '[data-filter-value-name]'
              );

            if (!filterValues.length) {
              return;
            }

            /*
             * Deduplicate values.
             */
            const filterValuesMap =
              new Map();

            filterValues.forEach(
              (value) => {
                const filterValueName =
                  value.dataset
                    .filterValueName;

                const filterId =
                  value.dataset.filter;

                if (
                  !filterValueName ||
                  !filterId
                ) {
                  return;
                }

                const normalizedName =
                  filterValueName
                    .trim()
                    .toLowerCase();

                if (
                  !filterValuesMap.has(
                    normalizedName
                  )
                ) {
                  filterValuesMap.set(
                    normalizedName,
                    {
                      filterValueName,
                      filterId,
                    }
                  );
                }
              }
            );

            /*
             * Replace Liquid items with Shopify's
             * actual current facet values.
             */
            filterSelect.innerHTML = '';

            filterValuesMap.forEach(
              ({
                filterValueName,
                filterId,
              }) => {
                filterSelect.append(
                  this.createFilterHtml(
                    filterId,
                    filterValueName
                  )
                );
              }
            );
          });
        }
      );

      /*
       * Bind events to the newly-created items.
       */
      this.bindFilterItems();
      this.bindSearchInputs();
    }

    /*
     * ============================================================
     * CREATE CUSTOM FILTER OPTION
     * ============================================================
     */
    createFilterHtml(
      filterId,
      label
    ) {
      const li =
        document.createElement('li');

      li.className =
        'filter-select__item';

      li.setAttribute(
        'tabindex',
        '0'
      );

      li.setAttribute(
        'role',
        'button'
      );

      li.setAttribute(
        'data-filter',
        filterId
      );

      li.innerText =
        label;

      return li;
    }
  }

  customElements.define(
    'search-filter',
    SearchFilter
  );
}